import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Star } from "lucide-react";
import Typewriter from "../Typewriter";

const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 + i * 0.12 },
  }),
};

const Line = ({ children, i }) => (
  <span className="line-mask">
    <motion.span
      className="block"
      custom={i}
      variants={lineVariant}
      initial="hidden"
      animate="visible"
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  return (
    <section data-testid="hero" className="relative min-h-screen overflow-hidden grain bg-black">
      <div className="pointer-events-none absolute left-1/2 top-1/3 h-[700px] w-[900px] -translate-x-1/2 rounded-full bg-white/[0.06] blur-[160px]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,#000_75%)]" />

      <div className="relative z-10 mx-auto flex min-h-screen max-w-[90rem] flex-col justify-center px-6 pt-28 md:px-12">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15 }}
          className="mb-8 flex flex-wrap items-center gap-4"
        >
          <span className="flex items-center gap-1">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={13} className="fill-white text-white" />
            ))}
          </span>
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-white/75">
            Mumbai · Est. 2020 · 200+ brands shipped
          </span>
        </motion.div>

        <h1 className="font-display font-extrabold leading-[0.9] tracking-tighter text-white text-[3rem] sm:text-6xl lg:text-[7.5rem]">
          <Line i={0}>Marketing that</Line>
          <Line i={1}>
            <span className="font-serif-emph font-normal">earns its keep,</span>
          </Line>
          <Line i={2}>
            <span className="text-stroke">quarter after quarter.</span>
          </Line>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-10 max-w-2xl text-lg leading-relaxed text-white/70"
        >
          <p>
            Adinvo is a Mumbai-built growth studio for{" "}
            <span className="text-white font-medium">
              <Typewriter
                words={[
                  "ambitious D2C brands",
                  "SaaS founders in a hurry",
                  "F&B challengers",
                  "fashion labels going global",
                  "clinics ready to scale",
                ]}
              />
            </span>
            .
          </p>
          <p className="mt-3">
            Ads, sites, apps and brand under one roof. Five years, 200+ launches,
            zero fluff — just senior operators who treat your P&amp;L like their own.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/booking"
            data-testid="hero-book-btn"
            className="group inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-base font-semibold text-black transition-colors hover:bg-white/90"
          >
            Book a growth diagnosis
            <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </Link>
          <button
            data-testid="hero-services-btn"
            onClick={() =>
              document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })
            }
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-8 py-4 text-base font-semibold text-white transition-colors hover:bg-white/5 hover:border-white/40"
          >
            See the work
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="mt-24 border-t border-white/10 pt-8"
        >
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/75">
            Trusted by ambitious brands across India, UK, USA &amp; UAE
          </p>
          <div className="mt-6 grid grid-cols-2 gap-y-4 text-white/65 sm:grid-cols-3 md:grid-cols-6">
            {["Riddhi", "Aarna", "Bloom", "NordicWear", "Vantage", "Spice Route"].map((b) => (
              <span key={b} className="font-display text-lg font-bold tracking-tight">
                {b}<span className="text-white/70">✦</span>
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
